import { Injectable } from '@angular/core';
import {
  BasketAction,
  BASKET_CATEGORY,
} from '@epicuro-next/constants/platform/tracking/basket';
import { BasketUploadService } from '@epicuro-next/data/baskets';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import {
  checkBasket,
  clearBasketSuccess,
  loadActiveBasket,
  selectDeliveryShopIds,
  setActiveBasketId,
  switchBasketSuccess,
  updateActiveBasketAccounts,
  updateDtoBasketSuccess,
  updateLastUsedBasketId,
} from '@epicuro-next/state/basket-state';
import { go } from '@epicuro-next/state/router-store';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { gtmTrackActions } from '@epicuro-next/state/tracking';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  checkMDOBasket,
  checkMDOBasketFailure,
  checkMDOBasketSuccess,
  importMDOBasket,
  importMDOBasketFailure,
  importMDOBasketSuccess,
  resetUploadBasketState,
  uploadSDOBasket,
  uploadSDOBasketFailure,
  uploadSDOBasketSuccess,
} from '../actions/upload-basket-actions';
import { selectMDOFileToken } from '../selectors/upload-basket.selectors';

@Injectable()
export class BasketUploadEffects {
  public uploadSDOBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadSDOBasket),
      withLatestFrom(
        this.store.pipe(select(selectActiveAccountId)),
        this.store.pipe(select(selectDeliveryShopIds)),
      ),
      switchMap(
        ([{ uploadBasket, addToExistingBasket }, accountId, addressIds]) => {
          return this.uploadBasketService
            .uploadSDOBasket(
              accountId as string,
              {
                ...uploadBasket,
                addressIds,
              },
              addToExistingBasket,
            )
            .pipe(
              map((basket) =>
                uploadSDOBasketSuccess({
                  basketId: basket.basketId,
                  validationResults: basket.validationResults,
                }),
              ),
              catchError((err) => [
                uploadSDOBasketFailure({ error: err.message }),
                showSnackBarMessage({
                  messageType: MessageType.DANGER,
                  message: $localize`:basketUpload|Basket sdo upload failure message@@uploadBasketFailMessage:Failed to upload basket`,
                }),
              ]),
            );
        },
      ),
    ),
  );

  public onUploadBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadSDOBasketSuccess, importMDOBasketSuccess),
      switchMap(({ basketId }) => [
        go({
          commands: [`basket/${basketId}`],
          extras: {
            state: {
              forceLeaveBasket: true,
            },
          },
        }),
        setActiveBasketId({ basketId }),
        updateLastUsedBasketId({ basketId }),
        loadActiveBasket(),
        updateActiveBasketAccounts(),
        gtmTrackActions.gtmTrackEvent({
          action: BasketAction.CREATE,
          properties: { category: BASKET_CATEGORY, label: basketId },
        }),
      ]),
    ),
  );

  public checkMDOBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkMDOBasket),
      withLatestFrom(this.store.pipe(select(selectActiveAccountId))),
      switchMap(([{ file }, accountId]) =>
        this.uploadBasketService
          .checkMdoBasketUpload(accountId as string, file)
          .pipe(
            map((mdoUploadCheckResult) =>
              checkMDOBasketSuccess({ mdoUploadCheckResult }),
            ),
            catchError((err) => {
              const errorMessage = err.error?.includes(
                'prohibited content-type',
              )
                ? $localize`:cBasket|Basket mdo check upload failure prohibited message@@checkUploadMDOBasketFailMessageProhibited:File is of prohibited content-type`
                : $localize`:cBasket|Basket mdo check upload failure message@@checkUploadMDOBasketFailMessage:Failed to check basket`;
              return [
                checkMDOBasketFailure({ error: err.message }),
                showSnackBarMessage({
                  messageType: MessageType.DANGER,
                  message: errorMessage,
                  hideDelay: 4000,
                }),
              ];
            }),
          ),
      ),
    ),
  );

  public importMDOBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(importMDOBasket),
      withLatestFrom(
        this.store.pipe(select(selectActiveAccountId)),
        this.store.pipe(
          select(selectMDOFileToken),
          filter((v): v is string => !!v),
        ),
      ),
      switchMap(([{ skipErrors, addToExistingBasket }, accountId, fileToken]) =>
        this.uploadBasketService
          .importMdoBasket(
            accountId as string,
            fileToken,
            skipErrors,
            addToExistingBasket,
          )
          .pipe(
            switchMap((basketId) => [
              importMDOBasketSuccess({ basketId }),
              showSnackBarMessage({
                messageType: MessageType.SUCCESS,
                message: $localize`:cBasket|Basket mdo upload success message@@uploadMDOBasketSuccessMessage:Basket uploaded successfully`,
              }),
            ]),
            catchError((err) => [
              importMDOBasketFailure({ error: err.message }),
              showSnackBarMessage({
                messageType: MessageType.DANGER,
                message: $localize`:cBasket|Basket mdo upload failure message@@uploadMDOBasketFailMessage:Failed to upload basket`,
              }),
            ]),
          ),
      ),
    ),
  );

  public clearUploadState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        checkBasket,
        updateDtoBasketSuccess,
        clearBasketSuccess,
        switchBasketSuccess,
      ),
      map(() => resetUploadBasketState()),
    ),
  );

  constructor(
    private actions$: Actions,
    private uploadBasketService: BasketUploadService,
    private store: Store,
  ) {}
}
